import { useRef, useState } from 'react'
import { clearFlashMessages, setFlashMessage, userIsLogged, userRedirectLogin } from '../../helpers'
import { API_ROUTES } from '../../constants/apiRoutes'
import { TRANS_SS_MODES_SAUTER } from '../../constants/translations'
import apiClient from '../../services/api/apiClient'
import { getSauterDeviceLabels } from '../../services/deviceLabelsService'
import House from './House';
import refreshPng from '../../resources/img/refresh.png'

const listTemperatures = Array(18).fill(0).map((val, index) => {
    let value = index + 10
    let valueMid = value + 0.5
    return (<><option value={value} >{value}</option> <option value={valueMid} >{valueMid}</option></>)
})

function getDateDayName(day) {
    return ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"][day]
}

const listDays = Array(7).fill(0).map((val, index) => {
    let dt = new Date()
    dt.setDate(dt.getDate() + index)
    let day = getDateDayName(dt.getDay())
    if (index === 0) {
        day = "Aujourd'hui"
    }
    if (index === 1) {
        day = "Demain"
    }

    const currentDtStr = day + ' ' + dt.getDate() + '/' + (dt.getMonth() + 1)
    if (index === 0) {
        return (<option value="today">{currentDtStr}</option>)
    }
    return (<option value={"today + " + index + " days"}>{currentDtStr}</option>)
})

export class HouseProgManagement {

    padTimeUnit(value) {
        return String(value).padStart(2, '0')
    }

    extractProgDateParts(dateInput) {
        const now = new Date()
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0)

        const normalizeDateString = (value) => {
            if (!value || typeof value !== 'string') {
                return null
            }
            if (value.includes(' ') && !value.includes('T')) {
                return value.replace(' ', 'T')
            }
            return value
        }

        const resolvedDateStr = (() => {
            if (typeof dateInput === 'string') {
                return normalizeDateString(dateInput)
            }
            if (dateInput && typeof dateInput === 'object' && dateInput.date) {
                return normalizeDateString(dateInput.date)
            }
            return null
        })()

        const dt = resolvedDateStr ? new Date(resolvedDateStr) : null
        if (!dt || Number.isNaN(dt.getTime())) {
            return { dayValue: 'today', hours: '00', minutes: '00' }
        }

        const diffDays = Math.round((dt.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24))
        const isWithinRange = diffDays >= 0 && diffDays <= 6
        const dayValue = diffDays === 0 ? 'today' : (isWithinRange ? `today + ${diffDays} days` : 'today')
        const hours = this.padTimeUnit(dt.getHours())
        const minutes = this.padTimeUnit(Math.min(50, Math.floor(dt.getMinutes() / 10) * 10))
        return { dayValue, hours, minutes }
    }

    handleTimeChange(event, setHours, setMinutes) {
        const [rawHours = '0', rawMinutes = '0'] = (event.target.value || '').split(':')
        const hoursInt = parseInt(rawHours, 10)
        const minutesInt = parseInt(rawMinutes, 10)
        const normalizedHours = Number.isNaN(hoursInt) ? 0 : Math.min(23, Math.max(0, hoursInt))
        const normalizedMinutes = Number.isNaN(minutesInt) ? 0 : Math.min(50, Math.floor(minutesInt / 10) * 10)
        setHours(this.padTimeUnit(normalizedHours))
        setMinutes(this.padTimeUnit(normalizedMinutes))
    }

    getTimeInputValue(hours, minutes) {
        return `${this.padTimeUnit(hours)}:${this.padTimeUnit(minutes)}`
    }

    requestEditProgrammation(progId, deviceName, progDay, progTimeHours, progTimeMinutes, progTarget, isProgRepeatDaily = false, onSuccess = null) {
        const payload = { progId, deviceName, progDay, progTimeHours, progTimeMinutes, progTarget, isProgRepeatDaily }
        apiClient.post(API_ROUTES.SAUTER_PROG_EDIT, payload).then(() => {
            this.loadProgsIntoTemplate()
            if (typeof onSuccess === 'function') {
                onSuccess()
            }
        }).catch(error => {
            let msg = 'Echec de la modification de la programmation';
            if (error.responseJSON && error.responseJSON.output) {
                msg += ' : ' + error.responseJSON.output;
            }
            setFlashMessage('error', msg);
        })
    }

    requestSaveProgrammation(deviceName, progDay, progTimeHours, progTimeMinutes, progTarget, isProgRepeatDaily = false) {
        const payload = { deviceName, progDay, progTimeHours, progTimeMinutes, progTarget, isProgRepeatDaily }
        apiClient.post(API_ROUTES.SAUTER_PROG_SAVE, payload).then(() => {
            this.loadProgsIntoTemplate()
        }).catch(error => {
            let msg = 'Echec de la création de la programmation';
            if (error.responseJSON && error.responseJSON.output) {
                msg += ' : ' + error.responseJSON.output;
            }
            setFlashMessage('error', msg);
        })
    }

    cancelDeviceProg(progId) {
        apiClient.post(API_ROUTES.SAUTER_PROG_CANCEL, { 'progId': progId }).then(() => {
            this.loadProgsIntoTemplate()
        }).catch(error => {
            let msg = 'Echec de l\'annulation de la programmation';
            if (error.responseJSON && error.responseJSON.output) {
                msg += ' : ' + error.responseJSON.output;
            }
            setFlashMessage('error', msg);
        })
    }

    saveProg() {
        let progTarget = this.programDeviceName === 'HS_SS' ? this.programTempSS : this.programTemp
        this.requestSaveProgrammation(this.programDeviceName, this.programDateDay, this.programDateHours, this.programDateMinutes, progTarget, this.programIsRepeatDaily)
    }

    setDeviceProgEnable(progId, enable) {
        apiClient.post(API_ROUTES.SAUTER_PROG_DISABLE_ENABLE, { progId, enable }).then(() => {
            this.loadProgsIntoTemplate()
        }).catch(error => {
            let msg = 'Echec de la désactivation/activation de la programmation';
            if (error.responseJSON && error.responseJSON.output) {
                msg += ' : ' + error.responseJSON.output;
            }
            setFlashMessage('error', msg);
        })
    }

    setAllDevicesProgEnable(enable) {
        const progIds = this._progs.map(item => item.id);

        if (progIds.length === 0) {
            setFlashMessage('warning', 'Aucune programmation à ' + (enable ? 'activer' : 'désactiver'));
            return;
        }

        let completed = 0;
        let errors = 0;

        progIds.forEach(progId => {
            apiClient.post(API_ROUTES.SAUTER_PROG_DISABLE_ENABLE, { progId, enable }).then(() => {
                completed++;
                if (completed + errors === progIds.length) {
                    this.loadProgsIntoTemplate();
                    if (errors === 0) {
                        setFlashMessage('success', progIds.length + ' programmation(s) ' + (enable ? 'activée(s)' : 'désactivée(s)'));
                    } else {
                        setFlashMessage('warning', completed + ' programmation(s) modifiée(s), ' + errors + ' erreur(s).');
                    }
                }
            }).catch(error => {
                errors++;
                if (completed + errors === progIds.length) {
                    this.loadProgsIntoTemplate();
                    setFlashMessage('error', 'Erreurs lors de la modification groupée : ' + errors + ' échec(s)');
                }
            });
        });
    }


    getProgsItemsToDisplay(onEditTemperatureProg) {
        if (this._progs) {
            return this._progs.map(item => {
                const deviceLabel = this._deviceLabels?.[item.operationIdentifier] || item.operationIdentifier
                return (
                    <tr className={(item.enabled !== true ? 'table-line-disabled' : '')}>
                        <td>
                            <div>{deviceLabel}</div>
                            <div>
                                <button
                                    className='btn btn-secondary btn-sm mt-1'
                                    title='Modifier'
                                    aria-label='Modifier la programmation'
                                    onClick={() => onEditTemperatureProg && onEditTemperatureProg(item)}>
                                    <i className="fa fa-pencil"></i>
                                </button>
                            </div>
                        </td>
                        <td>{item.dateTriggerLocal}
                            <div className={(item.repeatDaily !== true ? 'd-none' : '')}>
                                <i className="fa fa-repeat fa-lg"><br /></i>
                            </div>
                        </td>
                        <td>{item.operationIdentifier === 'HS_SS' ? TRANS_SS_MODES_SAUTER[item.operationValue] : item.operationValue}</td>
                        <td>
                            <button
                                className='btn btn-danger btn-sm'
                                title='Supprimer'
                                aria-label='Supprimer la programmation'
                                onClick={() => (this._onRequestCancel ? this._onRequestCancel(item.id) : this.cancelDeviceProg(item.id))}>
                                <i className="fa fa-trash"></i>
                            </button>
                            <button
                                className={'ml-2 mt-2 btn btn-warning btn-sm ' + (item.enabled === false ? 'd-none' : '')}
                                title='Désactiver'
                                aria-label='Désactiver la programmation'
                                onClick={() => this.setDeviceProgEnable(item.id, false)}>
                                <i className="fa fa-pause"></i>
                            </button>
                            <button
                                className={'ml-2 mt-2 btn btn-success btn-sm ' + (item.enabled === true ? 'd-none' : '')}
                                title='Activer'
                                aria-label='Activer la programmation'
                                onClick={() => this.setDeviceProgEnable(item.id, true)}>
                                <i className="fa fa-play"></i>
                            </button>
                        </td>
                    </tr >
                )
            })
        }

    }

    async loadProgs() {
        return new Promise((resolve, reject) => {
            let url = API_ROUTES.SAUTER_DEVICES_PROGS
            apiClient.get(url).then((data) => {
                resolve(data)
                const flashItem = document.querySelector('#flashMessages div')
                if (flashItem && flashItem.textContent.includes('Echec du chargement des données')) {
                    clearFlashMessages()
                }
            }).catch(error => {
                let msg = 'Echec du chargement des données de programmes (PROG)';
                if (error.responseJSON && error.responseJSON.output) {
                    msg += ' : ' + error.responseJSON.output;
                }
                setFlashMessage('error', msg);
                resolve()
            })
        })
    }

    loadProgsIntoTemplate() {
        this.loadProgs().then((data) => {
            if (data) {
                if (JSON.stringify(this._progs) === JSON.stringify(data)) {
                    return
                }
                this._setProgs(data)
            }
        })
    }


}

export default function HouseSauterProg() {

    const [isLogged, setIsLogged] = useState(false)

    // state for cancel confirmation modal
    const [showCancelModal, setShowCancelModal] = useState(false)
    const [cancelProgId, setCancelProgId] = useState(null)
    if (isLogged !== true) {
        userIsLogged().then((isLogged) => {
            if (isLogged === false) {
                userRedirectLogin()
            }
            setIsLogged(isLogged)
        })
    }


    const SSModeOptions = Object.keys(TRANS_SS_MODES_SAUTER).map((key) => {
        return <option value={key} key={key}>{TRANS_SS_MODES_SAUTER[key]}</option>
    })


    const REFRESH_INTERVAL = 30 * 1000
    let refRefreshIntervalId = useRef(null)
    const refLabelsLoadStarted = useRef(false)

    const manager = new HouseProgManagement()

    const [deviceLabels, setDeviceLabels] = useState({})
    const [deviceLabelsLoaded, setDeviceLabelsLoaded] = useState(false)

    const [progs, setProgs] = useState(null)
    const [currentDeviceNameSelected, setCurrentDeviceNameSelected] = useState('RAD_SALON')
    const [isProgRepeatDaily, setIsProgRepeatDaily] = useState(false)
    const [programTemp, setProgramTemp] = useState("STOP")
    const [programDateDay, setProgramDateDay] = useState("today")
    const [programDateHours, setProgramDateHours] = useState("00")
    const [programDateMinutes, setProgramDateMinutes] = useState("00")
    const [programTempSS, setProgramTempSS] = useState("off")
    const [isCreateOpen, setIsCreateOpen] = useState(false)

    const [editModalOpen, setEditModalOpen] = useState(false)
    const [editProg, setEditProg] = useState(null)
    const [editDay, setEditDay] = useState('today')
    const [editHours, setEditHours] = useState('00')
    const [editMinutes, setEditMinutes] = useState('00')
    const [editTempTarget, setEditTempTarget] = useState('STOP')
    const [editRepeatDaily, setEditRepeatDaily] = useState(false)

    manager._progs = progs
    manager._setProgs = setProgs
    manager._deviceLabels = deviceLabels

    manager.programTemp = programTemp
    manager.programDateDay = programDateDay
    manager.programDateHours = programDateHours
    manager.programDateMinutes = programDateMinutes
    manager.programIsRepeatDaily = isProgRepeatDaily
    manager.programDeviceName = currentDeviceNameSelected
    manager.programTempSS = programTempSS

    // allow manager to request a cancel confirmation modal
    manager._onRequestCancel = (id) => {
        setCancelProgId(id)
        setShowCancelModal(true)
    }

    if (isLogged && !deviceLabelsLoaded) {
        if (!refLabelsLoadStarted.current) {
            refLabelsLoadStarted.current = true
            getSauterDeviceLabels().then((labels) => {
                setDeviceLabels(labels)
            }).finally(() => {
                setDeviceLabelsLoaded(true)
            })
        }
    }

    // selected programmation details for the modal
    const selectedProg = (progs && cancelProgId != null) ? progs.find(p => p.id === cancelProgId) : null

    if (progs === null) {
        manager.loadProgsIntoTemplate()
    }

    if (refRefreshIntervalId?.current === null) {
        refRefreshIntervalId.current = setInterval(() => {
            manager.loadProgsIntoTemplate()
        }, REFRESH_INTERVAL)
    }

    if (isLogged) {

        const listDevices = Object.keys(deviceLabels).length > 0
            ? Object.entries(deviceLabels).map(([name, label]) => (
                <option value={name} key={name}>{label}</option>
            ))
            : []

        const timeInputValue = manager.getTimeInputValue(programDateHours, programDateMinutes)

        const openEditModal = (progItem) => {
            if (!progItem) return
            const { dayValue, hours, minutes } = manager.extractProgDateParts(progItem.dateTrigger)
            setEditProg(progItem)
            setEditDay(dayValue)
            setEditHours(hours)
            setEditMinutes(minutes)
            setEditTempTarget(progItem.operationValue)
            setEditRepeatDaily(progItem.repeatDaily === true)
            setEditModalOpen(true)
        }

        const closeEditModal = () => {
            setEditModalOpen(false)
            setEditProg(null)
        }

        const handleEditSave = () => {
            if (!editProg) return
            manager.requestEditProgrammation(
                editProg.id,
                editProg.operationIdentifier,
                editDay,
                editHours,
                editMinutes,
                editTempTarget,
                editRepeatDaily,
                () => closeEditModal()
            )
        }

        const editTimeInputValue = manager.getTimeInputValue(editHours, editMinutes)
        return (
            <>
                <House activeTab="prog" />
                <div className="conteneur">
                    <div id="flashMessages"></div>
                    <div id="devicesProgPart">
                        <div className="text-center" style={{ border: '3px solid rgb(255 255 255 / 71%)', borderRadius: '8px', padding: '10px', marginTop: '12px', background: 'rgba(13,110,253,0.06)', boxShadow: '0 0 0 1px rgba(13,110,253,0.12)' }}>
                            <div className="d-flex align-items-center justify-content-center mt-1"
                                onClick={() => setIsCreateOpen(!isCreateOpen)}>
                                <span className="mr-2">Ajout de programme</span>
                                <button
                                    type="button"
                                    className="btn btn-link btn-sm p-0"
                                    aria-expanded={isCreateOpen}
                                    aria-controls="progCreateForm"
                                    aria-label={isCreateOpen ? 'Masquer la creation' : 'Afficher la creation'}>
                                    <i
                                        className={isCreateOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'}
                                        style={{ color: 'rgba(255, 255, 255)' }}
                                    ></i>
                                </button>
                            </div>

                            {isCreateOpen && (
                                <div id="progCreateForm" className="row text-center mt-3">
                                    <div className="col-6 col-sm-6 col-md-3 mb-3">
                                        <i className="fa fa-home"></i>
                                        <select className="form-control w-75 d-inline-block" id="program_deviceName" defaultValue={currentDeviceNameSelected} onChange={(e) => { setCurrentDeviceNameSelected(e.target.value) }}>
                                            {listDevices}
                                        </select>
                                    </div>
                                    <div className="col-6 col-sm-6 col-md-4 mb-3">
                                        <i className="fa fa-thermometer-2"></i>

                                        <select className={'form-control w-75 ' + (currentDeviceNameSelected === 'HS_SS' ? 'd-none' : 'd-inline-block')} id="program_temp" defaultValue={manager.programTemp} onChange={(e) => { setProgramTemp(e.target.value) }}>
                                            <option value="STOP">ARRET</option>
                                            {listTemperatures}
                                        </select>
                                        <select className={'form-control w-75 ' + (currentDeviceNameSelected === 'HS_SS' ? 'd-inline-block' : 'd-none')} id="program_ss_temp" defaultValue={manager.programTempSS} onChange={(e) => { setProgramTempSS(e.target.value) }}>
                                            {SSModeOptions}
                                        </select>

                                    </div>
                                    <div className="col-12 col-sm-6 col-md-4 mb-1">
                                        <i className="fa fa-calendar"></i>
                                        <select className="form-control w-25 d-inline-block" id="program_date_day" defaultValue={manager.programDateDay} onChange={(e) => { setProgramDateDay(e.target.value) }}>
                                            {listDays}
                                        </select>
                                        <i className="fa fa-clock-o"></i>
                                        <input
                                            id="program_date_time"
                                            type="time"
                                            className="form-control d-inline-block"
                                            style={{ maxWidth: '100px' }}
                                            value={timeInputValue}
                                            step={600}
                                            onChange={(e) => manager.handleTimeChange(e, setProgramDateHours, setProgramDateMinutes)}
                                            aria-label="Horaire de programmation"
                                        />

                                        <label className="checkbox-inline ml-2">
                                            <input type="checkbox" id="program_repeat_daily" defaultValue={manager.programIsRepeatDaily} 
                                            onChange={(e) => { setIsProgRepeatDaily(!manager.programIsRepeatDaily) }} /> Répéter
                                        </label>


                                    </div>
                                    <div className="col-12 text-center mt-2">
                                        <button className="btn btn-primary" onClick={() => manager.saveProg()}>Programmer</button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-2 text-center" id="histoProg">
                            <hr />
                            <div className="mt-3 mb-3">
                                <button className="btn btn-success mr-2" onClick={() => manager.setAllDevicesProgEnable(true)}>
                                    <i className="fa fa-check"></i> Activer tout
                                </button>
                                <button className="btn btn-warning" onClick={() => manager.setAllDevicesProgEnable(false)}>
                                    <i className="fa fa-times"></i> Désactiver tout
                                </button>
                            </div>

                            <table className="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>Device</th>
                                        <th>Date</th>
                                        <th>Temp. cible</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manager.getProgsItemsToDisplay(openEditModal)}
                                </tbody>
                            </table>
                        </div>

                        {editModalOpen && (
                            <div
                                className="modal fade show"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    overflowY: 'auto',
                                    padding: '16px',
                                    height: '140vh',
                                    zIndex: 1050,
                                    marginLeft: '-5vw',
                                    marginTop: '-35vh'
                                }}>
                                <div className="modal-dialog modal-sm w-100" style={{ width: '100%', maxWidth: '360px', margin: '0 auto', maxHeight: '90vh', overflow: 'auto' }}>
                                    <div className="modal-content">
                                        <div className="modal-header">
                                            <h5 className="modal-title">Modifier la programmation</h5>
                                            <button type="button" className="close" onClick={closeEditModal} aria-label="Fermer">
                                                <span aria-hidden="true">&times;</span>
                                            </button>
                                        </div>
                                        <div className="modal-body">
                                            <div className="mb-2">
                                                <strong>{editProg?.operationIdentifier && (manager._deviceLabels?.[editProg.operationIdentifier] || editProg.operationIdentifier)}</strong>
                                            </div>
                                            <div className="form-group">
                                                <label className="d-block">Date</label>
                                                <select className="form-control form-control-sm" value={editDay} onChange={(e) => setEditDay(e.target.value)}>
                                                    {listDays}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label className="d-block">Heure</label>
                                                <input
                                                    type="time"
                                                    className="form-control form-control-sm"
                                                    value={editTimeInputValue}
                                                    step={600}
                                                    onChange={(e) => manager.handleTimeChange(e, setEditHours, setEditMinutes)}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <label className="d-block">Température / consigne</label>
                                                <select className={'form-control form-control-sm ' + (editProg?.operationIdentifier === 'HS_SS' ? 'd-none' : 'd-block')} value={editTempTarget} onChange={(e) => setEditTempTarget(e.target.value)}>
                                                    <option value="STOP">ARRET</option>
                                                    {listTemperatures}
                                                </select>
                                                <select className={'form-control form-control-sm ' + (editProg?.operationIdentifier === 'HS_SS' ? 'd-block' : 'd-none')} value={editTempTarget} onChange={(e) => setEditTempTarget(e.target.value)}>
                                                    {SSModeOptions}
                                                </select>
                                            </div>
                                            <div className="form-check">
                                                <input className="form-check-input" type="checkbox" id="edit_repeat_daily" checked={editRepeatDaily} onChange={() => setEditRepeatDaily(!editRepeatDaily)} />
                                                <label className="form-check-label" htmlFor="edit_repeat_daily">Répéter tous les jours</label>
                                            </div>
                                        </div>
                                        <div className="modal-footer">
                                            <button className="btn btn-secondary" onClick={closeEditModal}>Annuler</button>
                                            <button className="btn btn-primary btn-sm" onClick={handleEditSave}>Enregistrer</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Confirmation modal for cancelling a programmation */}
                        {showCancelModal && (
                            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100002, backdropFilter: 'blur(2px)', pointerEvents: 'auto' }}>
                                <div className="modal-dialog" role="dialog" aria-modal="true" style={{ background: 'rgba(255,255,255,0.96)', padding: '20px', borderRadius: '8px', maxWidth: '500px', width: '90%', boxShadow: '0 6px 20px rgba(0,0,0,0.18)', color: '#000', pointerEvents: 'auto' }}>
                                    <h5 style={{ color: '#000' }}>Confirmer l'annulation</h5>
                                    {selectedProg ? (
                                        <div style={{ color: '#000' }}>
                                            <p><strong>Appareil :</strong> {manager._deviceLabels?.[selectedProg.operationIdentifier] || selectedProg.operationIdentifier}</p>
                                            <p><strong>Action :</strong> {selectedProg.operationName} {selectedProg.operationIdentifier === 'HS_SS' ? TRANS_SS_MODES_SAUTER[selectedProg.operationValue] : selectedProg.operationValue}</p>
                                            <p><strong>Date / Heure du programme :</strong> {selectedProg.dateTriggerLocal}</p>
                                            <p><strong>Se répète tous les jours ? :</strong> {selectedProg.repeatDaily ? 'Oui' : 'Non'}</p>
                                        </div>
                                    ) : (
                                        <p style={{ color: '#000' }}>Voulez-vous vraiment annuler cette programmation ?</p>
                                    )}
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                                        <button className="btn btn-secondary" onClick={() => { setShowCancelModal(false); setCancelProgId(null); }}>Annuler</button>
                                        <button className="btn btn-danger" onClick={() => { manager.cancelDeviceProg(cancelProgId); setShowCancelModal(false); setCancelProgId(null); }}>Confirmer</button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="text-center small">
                            <img onClick={() => { manager.loadProgsIntoTemplate() }} src={refreshPng} className="cursor-pointer"
                                style={{ width: "24px" }} alt="Forcer à rafraichir" />
                        </div>

                    </div>
                </div>

            </>)
    }
}
